import { ActionIcon, Divider, Flex, Group, Paper, Stack, Text, Tooltip } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { ReactNode, useCallback } from "react";
import { formatCurrency } from "utils/helpers/format";

interface BulkReceiptGroupProps {
  debtCode: string;
  partnerName: string;
  saleOrderCode: string;
  /** Số dư còn lại của công nợ sau khi trừ tổng số tiền của tất cả phiếu thu trong nhóm. */
  remaining: number;
  /** Vị trí dòng cuối cùng của nhóm trong mảng items — dùng để chèn phiếu thu mới ngay sau nó. */
  groupEndIndex: number;
  onAddReceipt: (index: number) => void;
  canAddMore: boolean;
  children: ReactNode;
}

/**
 * Khung công nợ (mã công nợ, khách hàng, đơn bán hàng, số dư còn lại) làm cha
 * chung cho các phiếu thu (BulkReceiptRow) của cùng một công nợ, thay vì lặp
 * lại thông tin công nợ trên từng dòng.
 */
const BulkReceiptGroup = ({
  debtCode,
  partnerName,
  saleOrderCode,
  remaining,
  groupEndIndex,
  onAddReceipt,
  canAddMore,
  children,
}: BulkReceiptGroupProps) => {
  const handleAdd = useCallback(
    () => onAddReceipt(groupEndIndex),
    [onAddReceipt, groupEndIndex],
  );

  return (
    <Paper withBorder p="sm" bg="gray.0">
      <Stack gap="sm">
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap="xs">
          <Stack gap={2}>
            <Text size="sm" fw={700}>
              {debtCode} — {partnerName}
            </Text>
            <Text size="xs" c="dimmed">
              Đơn bán hàng: {saleOrderCode}
            </Text>
          </Stack>

          <Group gap="xs" wrap="nowrap">
            <Text size="sm" c="dimmed">
              Còn lại:{" "}
              <Text span fw={700} c="red">
                {formatCurrency(remaining)}
              </Text>
            </Text>
            <Tooltip label="Thêm đợt thu cho công nợ này" withArrow>
              <ActionIcon
                variant="light"
                color="blue"
                onClick={handleAdd}
                disabled={!canAddMore}
                aria-label="Thêm đợt thu"
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Flex>

        <Divider />

        <Stack gap="md">{children}</Stack>
      </Stack>
    </Paper>
  );
};

export default BulkReceiptGroup;
